// Currency formatter
export const currency = (n) => `₹${Number(n).toLocaleString()}`;

// Currency formatter with truncation to 2 decimals (no rounding)
export const currencyRounded = (value) => {
  const num = Number(value);
  if (isNaN(num)) return '₹0';
  const truncated = Math.trunc(num * 100) / 100;
  const fixed = truncated.toFixed(2);
  const [wholePart, decimalPart] = fixed.split('.');
  const formattedWhole = Number(wholePart).toLocaleString('en-IN');
  if (decimalPart === '00') {
    return `₹${formattedWhole}`;
  }
  return `₹${formattedWhole}.${decimalPart}`;
};

// Format date and time as DD-MMM-YYYY HH:MM AM/PM (12-hour format)
export const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  
  // Handle case where dateString is an object with start_date and end_date
  if (typeof dateString === 'object' && dateString !== null && !(dateString instanceof Date)) {
    if (dateString.start_date && dateString.end_date) {
      let start = dateString.start_date;
      let end = dateString.end_date;
      
      if (typeof start === 'object' && start !== null && !(start instanceof Date)) {
        start = start.start_date || start.end_date || JSON.stringify(start);
      }
      if (typeof end === 'object' && end !== null && !(end instanceof Date)) {
        end = end.start_date || end.end_date || JSON.stringify(end);
      }
      
      const formattedStart = formatDateTime(String(start));
      const formattedEnd = formatDateTime(String(end));
      return `${formattedStart} to ${formattedEnd}`;
    } else {
      return 'N/A';
    }
  }
  
  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Check if the date is already in DD-MM-YYYY HH:MM format (from API)
    if (typeof dateString === 'string' && /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}$/.test(dateString)) {
      const [datePart, timePart] = dateString.split(' ');
      const [day, monthNum, year] = datePart.split('-');
      const [hours24, minutes] = timePart.split(':');
      
      const month = monthNames[parseInt(monthNum, 10) - 1] || monthNum;
      
      let hours = parseInt(hours24, 10);
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const formattedHours = String(hours).padStart(2, '0');
      
      return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
    }
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const formattedHours = String(hours).padStart(2, '0');
    
    return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
  } catch {
    return 'N/A';
  }
};

// Capitalize words
export const capitalizeWords = (str) => {
  if (!str) return '';
  return String(str)
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Capitalize gender
export const capitalizeGender = (gender) => {
  if (!gender) return 'N/A';
  const genderStr = String(gender).toLowerCase();
  if (genderStr === 'm' || genderStr === 'male') return 'Male';
  if (genderStr === 'f' || genderStr === 'female') return 'Female';
  if (genderStr === 'o' || genderStr === 'other') return 'Other';
  return capitalizeWords(gender);
};

// Format gender for API
export const formatGenderForApi = (gender) => {
  if (!gender) return '';
  const genderStr = String(gender).toLowerCase().trim();
  if (genderStr === 'male' || genderStr === 'm') return 'Male';
  if (genderStr === 'female' || genderStr === 'f') return 'Female';
  if (genderStr === 'other' || genderStr === 'o') return 'Other';
  // Fallback: capitalize first letter of each word
  return genderStr.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

// Status badge styling helper
export const getStatusBadgeStyle = (status) => {
  const statusLower = String(status || '').toLowerCase().trim();
  
  const statusColorMap = {
    'success':              { text: '#16A34A', bg: 'rgba(22, 163, 74, 0.1)' },
    'complete':             { text: '#15803D', bg: 'rgba(21, 128, 61, 0.1)' },
    'delivered':            { text: '#22C55E', bg: 'rgba(34, 197, 94, 0.1)' },
    'failed':               { text: '#DC2626', bg: 'rgba(220, 38, 38, 0.15)' },
    'cancelled':            { text: '#DC2626', bg: 'rgba(220, 38, 38, 0.15)' },
    'in_progress':          { text: '#D97706', bg: 'rgba(217, 119, 6, 0.12)' },
    'in progress':          { text: '#D97706', bg: 'rgba(217, 119, 6, 0.12)' },
    'payment pending':      { text: '#D97706', bg: 'rgba(217, 119, 6, 0.12)' },
    'payment_pending':      { text: '#D97706', bg: 'rgba(217, 119, 6, 0.12)' },
    'expired':              { text: '#9CA3AF', bg: 'rgba(156, 163, 175, 0.15)' },
    'refund pending':       { text: '#EA580C', bg: 'rgba(234, 88, 12, 0.12)' },
    'refund_pending':       { text: '#EA580C', bg: 'rgba(234, 88, 12, 0.12)' },
    'report generated':     { text: '#4F46E5', bg: 'rgba(79, 70, 229, 0.1)' },
    'report_generated':     { text: '#4F46E5', bg: 'rgba(79, 70, 229, 0.1)' },
    'initiated':            { text: '#3B82F6', bg: 'rgba(59, 130, 246, 0.1)' },
    'refunded':             { text: '#0EA5E9', bg: 'rgba(14, 165, 233, 0.1)' },
    'ordered':              { text: '#6366F1', bg: 'rgba(99, 102, 241, 0.1)' },
    'processed':            { text: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.1)' },
    'dispatched':           { text: '#14B8A6', bg: 'rgba(20, 184, 166, 0.1)' },
    'out_for_delivery':     { text: '#F97316', bg: 'rgba(249, 115, 22, 0.1)' },
    'out for delivery':     { text: '#F97316', bg: 'rgba(249, 115, 22, 0.1)' },
    'out_of_stock':         { text: '#B91C1C', bg: 'rgba(185, 28, 28, 0.1)' },
    'out of stock':         { text: '#B91C1C', bg: 'rgba(185, 28, 28, 0.1)' },
    'dropped':              { text: '#7C2D12', bg: 'rgba(124, 45, 18, 0.1)' },
    'saved':                { text: '#6B7280', bg: 'rgba(107, 114, 128, 0.1)' },
    // Diagnostic lab status flow
    'confirmed':            { text: '#0891B2', bg: 'rgba(8, 145, 178, 0.1)' },
    'cancelled_by_lab':     { text: '#DC2626', bg: 'rgba(220, 38, 38, 0.15)' },
    'cancelled by lab':     { text: '#DC2626', bg: 'rgba(220, 38, 38, 0.15)' },
    'sample_collected':     { text: '#0EA5E9', bg: 'rgba(14, 165, 233, 0.1)' },
    'sample collected':     { text: '#0EA5E9', bg: 'rgba(14, 165, 233, 0.1)' },
    'completed':            { text: '#15803D', bg: 'rgba(21, 128, 61, 0.1)' },
  };
  
  const colorConfig = statusColorMap[statusLower] || statusColorMap['saved'];

  return {
    color: colorConfig.text,
    backgroundColor: colorConfig.bg,
    borderColor: colorConfig.text,
    minWidth: '100px',
    justifyContent: 'center',
  };
};

// Capitalize relationship
export const capitalizeRelationship = (relationship) => {
  if (!relationship) return 'N/A';
  const relStr = String(relationship).trim();
  if (!relStr) return 'N/A';
  const formatted = formatRelationshipForApi(relStr);
  return REL_LABELS[formatted] || capitalizeWords(relStr);
};

// Format status
export const formatStatus = (status) => {
  if (!status) return 'N/A';
  const normalized = String(status).trim().toLowerCase().replace(/\s+/g, ' ');
  if (normalized === 'success' || normalized === 'successful') return 'Complete';
  if (normalized === 'approved' || normalized === 'processed') return 'Approved';
  if (normalized === 'rejected' || normalized === 'cancelled') return 'Rejected';
  if (normalized === 'pending') return 'Pending';
  if (normalized === 'in_progress' || normalized === 'in progress' || normalized.includes('progress')) return 'In Progress';
  return capitalizeWords(status);
};

// Calculate age from DOB
export const calculateAgeFromDob = (dobString) => {
  if (!dobString) return null;
  try {
    let d;
    
    // Handle DD/MM/YYYY format (with slashes) - from API
    if (dobString.includes('/')) {
      const [dd, mm, yyyy] = dobString.split('/');
      d = new Date(`${yyyy}-${mm}-${dd}`);
    } 
    // Handle YYYY-MM-DD format (ISO date format from date input)
    else {
      d = new Date(dobString);
    }
    
      if (isNaN(d.getTime())) return null;
    
      const today = new Date();
      let age = today.getFullYear() - d.getFullYear();
      const m = today.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < d.getDate())) {
        age--;
      }
      return age;
  } catch {
    return null;
  }
};

// Relationship labels mapping
export const REL_LABELS = {
  'self': 'Self',
  'father': 'Father',
  'mother': 'Mother',
  'spouse': 'Spouse',
  'son': 'Son',
  'daughter': 'Daughter',
  'brother': 'Brother',
  'sister': 'Sister',
  'mother_in_law': 'Mother-in-Law',
  'father_in_law': 'Father-in-Law',
  'son_in_law': 'Son-in-Law',
  'daughter_in_law': 'Daughter-in-Law',
  'brother_in_law': 'Brother-in-Law',
  'sister_in_law': 'Sister-in-Law',
  'grandfather': 'Grandfather',
  'grandmother': 'Grandmother',
  'grandson': 'Grandson',
  'granddaughter': 'Granddaughter',
  'uncle': 'Uncle',
  'aunt': 'Aunt',
  'nephew': 'Nephew',
  'niece': 'Niece',
  'cousin': 'Cousin',
  'other': 'Other'
};

// Normalize relationship for API
export const normalizeRelationshipForApi = (label) => {
  if (!label) return '';
  const raw = String(label).trim();
  const key = raw.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  if (REL_LABELS[key]) return key;
  const values = Object.values(REL_LABELS);
  const foundLabel = values.find(l => l.toLowerCase() === raw.toLowerCase());
  if (foundLabel) {
    return Object.keys(REL_LABELS).find(k => REL_LABELS[k] === foundLabel) || key;
  }
  return key;
};

// Format relationship for API
export const formatRelationshipForApi = (valueOrLabel) => {
  if (!valueOrLabel) return '';
  const raw = String(valueOrLabel).trim();
  const key = raw.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
  if (REL_LABELS[key]) return key;
  const values = Object.values(REL_LABELS);
  const foundLabel = values.find(l => l.toLowerCase() === raw.toLowerCase());
  if (foundLabel) {
    return Object.keys(REL_LABELS).find(k => REL_LABELS[k] === foundLabel) || key;
  }
  return key;
};

// Get existing relation label
export const getExistingRelationLabel = (selectedMember, family) => {
  if (!selectedMember || !family) return null;
  try {
    const selId = (selectedMember.member_id || selectedMember.id || '').toString();
    const fm = family?.find(m => ((m.member_id || m.id || '').toString() === selId));
    if (fm) return REL_LABELS[normalizeRelationshipForApi(fm.relationship || fm.relation)] || null;
    const selName = (selectedMember.name || '').toString().toLowerCase().trim();
    if (selName) {
      const fmByName = family?.find(m => (m.name || '').toString().toLowerCase().trim() === selName);
      if (fmByName) return REL_LABELS[normalizeRelationshipForApi(fmByName.relationship || fmByName.relation)] || null;
    }
  } catch {}
  return null;
};

// Convert date to input format
export const convertDateToInputFormat = (dateString) => {
  if (!dateString) return '';
  try {
    if (dateString.includes('/')) {
      const [dd, mm, yyyy] = dateString.split('/');
      return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
};

// Format DOB
export const formatDOB = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let date;
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      const day = parts[0].padStart(2, '0');
      const monthNum = parts[1].padStart(2, '0');
      let year = parts[2];
      if (year.length === 2) {
        const yearNum = parseInt(year, 10);
        year = yearNum > 50 ? `19${year}` : `20${year}`;
      }
      date = new Date(`${year}-${monthNum}-${day}`);
      if (!isNaN(date.getTime())) {
        const month = monthNames[date.getMonth()];
        return `${day}-${month}-${year}`;
      }
    }
    date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return 'N/A';
  }
};

// Format date
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return 'N/A';
  }
};

// Mask mobile number
export const maskMobile = (value) => {
  if (!value) return 'N/A';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length <= 4) return `******${digits}`;
  const last4 = digits.slice(-4);
  return `******${last4}`;
};

// Get current role
export const getCurrentRole = () => {
  const authRole = localStorage.getItem('authRole');
  const roleMapping = {
    'wallet_superadmin': 'SuperAdmin',
    'wallet_finance': 'Finance', 
    'wallet_ops': 'Ops',
    'wallet_support': 'Support'
  };
  return roleMapping[authRole] || null;
};

// Singleton relations
export const SINGLETON_RELATIONS = ['self', 'father', 'mother', 'spouse', 'mother_in_law', 'father_in_law'];

// Mobile uniqueness sub-groups:
// Group 1 — Subscriber family: Self, Spouse, Son, Daughter, Father, Mother (shared number allowed within group)
// Group 2 — Siblings: Brother, Sister (each must have a unique number — unique from all other members including other siblings)
// Group 3 — In-laws: Father-in-Law, Mother-in-Law (shared number allowed within group)
// Cross-group rule: numbers must be unique across groups (Group 1 ≠ Group 3, any ≠ Group 2)
export const MOBILE_SUBGROUP_MAP = {
  self: 1, spouse: 1, son: 1, daughter: 1, father: 1, mother: 1,
  father_in_law: 3, mother_in_law: 3,
  brother: 2,
  sister: 2,
};

// Returns the existing member that conflicts with the given mobile+relationship, or null.
// Group 2 (siblings) must be unique from everyone — including other siblings and all other groups.
// Group 1 and Group 3 members each share a pool internally; duplicates are only flagged across groups.
export const findMobileSubgroupDuplicate = (mobile, relationship, existingMembers, excludeId = null) => {
  if (!mobile || !relationship || !existingMembers?.length) return null;
  const cleaned = mobile.replace(/\D/g, '');
  if (cleaned.length !== 10) return null;
  const relKey = normalizeRelationshipForApi(relationship);
  const myGroup = MOBILE_SUBGROUP_MAP[relKey];
  if (myGroup == null) return null;

  return existingMembers.find(m => {
    const mId = m.id || m.member_id;
    if (excludeId != null && String(mId) === String(excludeId)) return false;
    const mRelKey = normalizeRelationshipForApi(m.relationship || m.relation || '');
    const mGroup = MOBILE_SUBGROUP_MAP[mRelKey];
    if (mGroup == null) return false;
    const existing = (m.mobile || m.contact_number || m.contact || m.phone || '').replace(/\D/g, '');
    if (!existing || existing !== cleaned) return false;
    // Siblings must be unique from everyone (within Group 2 and across all groups)
    if (myGroup === 2 || mGroup === 2) return true;
    // Group 1 and Group 3 allow sharing within the same group; conflict only when crossing groups
    return myGroup !== mGroup;
  }) ?? null;
};

// ── Subscriber ⇄ module detail navigation ──
// HISTORICAL. A medicine/diagnostics row in the subscriber's Transaction History used to
// NAVIGATE to the module page, carrying its origin in the URL so that page could offer a
// "Back to Subscriber" button. It no longer does — the subscriber page now renders the
// module's own detail popup in place, so nothing produces these URLs any more.
//
// What is left here is the READER half, kept on purpose: this is an internal ops tool and
// those links are already pasted into tickets and Slack threads. Dropping the reader would
// make them land silently on the module's list with no error. Retire it once logs show no
// traffic on `?from=subscriber`.
export const BOOKING_ORIGIN_SUBSCRIBER = 'subscriber';

// Transaction History tabs. Shared so the URL round-trip and the tab strip cannot drift.
export const TRANSACTION_CATEGORY_KEYS = [
  'All',
  'Diagnostics',
  'Medicines',
  'Hospital',
  'Loans',
  'Hospital Cash Allowance',
];

export const DIAGNOSTIC_MODULE_ROLES = ['Ops', 'SuperAdmin', 'Support'];

export const resolveBookingBackTarget = (searchParams, moduleFallback) => {
  if (searchParams?.get?.('from') !== BOOKING_ORIGIN_SUBSCRIBER) return moduleFallback;

  const subscriberId = searchParams.get('from_id');
  if (!subscriberId) return moduleFallback;

  const restored = new URLSearchParams();
  const category = searchParams.get('from_txn_category');
  const page = searchParams.get('from_txn_page');
  if (category && TRANSACTION_CATEGORY_KEYS.includes(category)) restored.set('txn_category', category);
  if (page && Number(page) > 1) restored.set('txn_page', page);

  const query = restored.toString();
  return {
    label: 'Back to Subscriber',
    path: `/subscriber-wallet/subscriber/${encodeURIComponent(subscriberId)}${query ? `?${query}` : ''}`,
  };
};

// The subscriber page's Transaction History tab/page as restored from the URL, falling
// back to the defaults when the user arrived normally or the params are not recognised.
export const readTransactionListState = (searchParams) => {
  const category = searchParams?.get?.('txn_category');
  const page = Number(searchParams?.get?.('txn_page'));
  return {
    txnCategory: TRANSACTION_CATEGORY_KEYS.includes(category) ? category : 'All',
    txnPage: Number.isInteger(page) && page > 0 ? page : 1,
  };
};

export const writeTransactionListState = (searchParams, { txnCategory, txnPage }) => {
  const next = new URLSearchParams(searchParams);
  if (txnCategory && txnCategory !== 'All') next.set('txn_category', txnCategory);
  else next.delete('txn_category');
  if (Number(txnPage) > 1) next.set('txn_page', String(txnPage));
  else next.delete('txn_page');
  return next;
};

// A list's URL (page + filters) saved when a row is opened, so the detail page's back button
// returns to the same page even after a round trip through another page that links back
// without router state.
export const SUBSCRIBER_LIST_PATH = '/subscriber-wallet/subscribers';
export const SUBSCRIBER_LIST_URL_KEY = 'subscriberList:url';
export const MEDICINE_LIST_PATH = '/subscriber-wallet/medicine';
export const MEDICINE_LIST_URL_KEY = 'medicineSubscribersList:url';
export const DIAGNOSTIC_LIST_PATH = '/subscriber-wallet/diagnostics';
export const DIAGNOSTIC_LIST_URL_KEY = 'diagnosticSubscribersList:url';
export const DOCTOR_LIST_PATH = '/subscriber-wallet/doctors';
export const DOCTOR_LIST_URL_KEY = 'doctorList:url';
export const HOSPITAL_PAYMENT_LIST_PATH = '/subscriber-wallet/hospital-payment';
export const HOSPITAL_PAYMENT_LIST_URL_KEY = 'hospitalPaymentList:url';
export const HOSPITAL_LIST_PATH = '/subscriber-wallet/hospitals';
export const HOSPITAL_LIST_URL_KEY = 'hospitalList:url';

const isListUrl = (value, listPath) =>
  typeof value === 'string' && (value === listPath || value.startsWith(`${listPath}?`));

export const resolveListBackTarget = (locationState, listPath, urlKey) => {
  if (isListUrl(locationState?.from, listPath)) return locationState.from;
  try {
    const saved = sessionStorage.getItem(urlKey);
    if (isListUrl(saved, listPath)) return saved;
  } catch {
    // sessionStorage unavailable — fall back to the first page.
  }
  return listPath;
};
